import React, { useMemo } from 'react';
import { getRootUrl } from '@utils/urlHelpers.util';
import { Flex } from '@components/Flex';
import {
  GridContainer,
  ItemPerLine,
  GridItem,
  GridItemContainer,
  ProductTile,
  ImageWrapper,
  ImageContainer,
  ProductPhoto,
  StyledFlex,
  BrandText,
  TitleText,
  ItemWrapper,
  HiddenText,
} from './GridView.styles';
import { addRendition } from '@utils/photoRendition';
import { Integrations } from '@static/js/types';
import { FormattedCurrency } from '@utils/currency';
import { useTranslation } from 'react-i18next';
import { FREE_SHIPPING_THRESHOLD } from '@utils/storefront/storefront.config';

const analytics = require('/static/js/joy');

export enum StoreFront {
  wedding = 'wedding',
  baby = 'baby',
}

export interface RecommendProduct {
  eventName: string;
  storeFront: StoreFront;
  node: {
    catalogItemGroupData: {
      tileViewItem: {
        id: string;
        title: string;
        publicUrlPath: string;
        brand: {
          name: string;
          publicUrlPath?: string;
        };
        photos?: {
          url: string;
        }[];
        mediaAssets?: {
          photo?: {
            url: string;
          };
        }[];
        price?: {
          currency: {
            code: string;
            places: number;
          };
          floatingPointDecimalString: string;
          valueInMinorUnits: number;
        };
      };
    };
  };
  enabledAmplitude?: boolean;
  displayPrice?: boolean;
}

[];

export type RecommendProducts = RecommendProduct[];

export type MonetaryValue =
  | {
      __typename?: 'MonetaryValue' | undefined;
      floatingPointDecimalString: string;
      valueInMinorUnits: number;
      currency?:
        | {
            __typename?: 'Currency' | undefined;
            code: string;
            places: number;
          }
        | null
        | undefined;
    }
  | null
  | undefined;

const ListItem: React.FC<RecommendProduct> = ({ node, eventName, storeFront, enabledAmplitude = false, displayPrice = false }) => {
  const { id, title, publicUrlPath, brand, photos, mediaAssets, price } = node.catalogItemGroupData.tileViewItem;
  const url = useMemo(() => (photos ? photos?.[0]?.url : mediaAssets?.[0]?.photo?.url), [mediaAssets, photos]);
  const shopUrl = useMemo(
    () => `${publicUrlPath}${storeFront === StoreFront.baby ? '?shopStorefront=baby' : ''}`,
    [publicUrlPath, storeFront]
  );

  const handleOnClick = () => {
    analytics.segment.sendTelemetryEvent(eventName, { name: title, value: shopUrl }, undefined, {
      integrations: { [Integrations.Amplitude]: enabledAmplitude },
    });
  };
  const formatPrice = (price: MonetaryValue) => {
    return FormattedCurrency({
      priceFloatingPointDecimalString: price?.floatingPointDecimalString || '',
      priceCurrencyCode: price?.currency?.code,
      formatForm: 'split',
      showItemSchema: true,
    });
  };

  const { t } = useTranslation('gridView');

  return (
    <>
      {url && (
        <span itemScope itemType="https://schema.org/Product">
          <ItemWrapper>
            <GridItem
              onClick={handleOnClick}
              forwardedAs="a"
              href={getRootUrl(shopUrl)}
              id={`GridItemLink${id}`}
              aria-label={`${brand.name} ${title}`}
              target="_blank"
            >
              <GridItemContainer>
                <ProductTile>
                  <Flex flexDirection="column">
                    <ImageWrapper backgroundColor="white" justifyContent="center" alignItems="center">
                      <ImageContainer justifyContent="center" alignItems="center">
                        <ProductPhoto
                          src={addRendition({ url, renditionSize: 'medium', isProduct: true })}
                          itemProp="image"
                          alt={t('productPhotoAlt')}
                        />
                      </ImageContainer>
                    </ImageWrapper>
                    <StyledFlex flexDirection="column" alignItems="center" marginTop={5} justifyContent="start">
                      <BrandText color="mono1" variant="dek20" tagName="h3" textAlign="center">
                        <span itemProp="brand" itemScope itemType="https://schema.org/Brand">
                          <span itemProp="name">{brand.name}</span>
                        </span>
                      </BrandText>
                      <TitleText color="mono1" variant="dek20" tagName="h3" textAlign="center">
                        <span itemProp="name">{title}</span>
                      </TitleText>
                      <HiddenText itemProp="description">{title}</HiddenText>
                      {displayPrice && (
                        <span itemProp="offers" itemType="https://schema.org/Offer" itemScope>
                          <HiddenText itemProp="priceCurrency">{price?.currency?.code}</HiddenText>
                          <BrandText color="mono1" variant="dek20" tagName="h3" textAlign="center" marginTop={1}>
                            <span itemProp="price" content={price?.floatingPointDecimalString}>
                              {formatPrice(price)}
                            </span>
                          </BrandText>
                          <HiddenText>
                            <link itemProp="availability" href="https://schema.org/InStock" />
                            <span itemProp="hasMerchantReturnPolicy" itemScope itemType="https://schema.org/MerchantReturnPolicy">
                              <link itemProp="returnPolicyCategory" href="https://schema.org/MerchantReturnFiniteReturnWindow" />
                              <span itemProp="merchantReturnLink">{t('merchantReturnLink')}</span>
                              <meta itemProp="merchantReturnDays" content="90" />
                              <span itemProp="applicableCountry" itemScope itemType="https://schema.org/Country">
                                <meta itemProp="name" content="US" />
                              </span>
                            </span>
                            <span itemProp="shippingDetails" itemScope itemType="https://schema.org/OfferShippingDetails">
                              {t('shippingDetailsLink')}
                              <span itemProp="shippingDestination" itemScope itemType="https://schema.org/DefinedRegion">
                                {t('shippingDestination')}
                                <span itemProp="addressCountry">US</span>
                              </span>
                              <span itemProp="shippingRate" itemScope itemType="https://schema.org/MonetaryAmount">
                                <span itemProp="value">{t('shippingRate')}</span>
                                <span itemProp="currency">{t('currency')}</span>
                              </span>
                              {t('shippingRateText', { price: FREE_SHIPPING_THRESHOLD })}
                            </span>
                          </HiddenText>
                        </span>
                      )}
                    </StyledFlex>
                  </Flex>
                </ProductTile>
              </GridItemContainer>
            </GridItem>
          </ItemWrapper>
        </span>
      )}
    </>
  );
};

interface GridViewProps {
  items: RecommendProducts;
  itemPerLine: ItemPerLine;
  eventName: string;
  storeFront: StoreFront;
  enabledAmplitude?: boolean;
  displayPrice?: boolean;
}

export const GridView: React.FC<GridViewProps> = ({ items, itemPerLine, ...restProps }) => {
  return (
    <GridContainer itemPerLine={itemPerLine} marginX="auto">
      {items.map((item, index) => {
        return (
          item.node.catalogItemGroupData &&
          item.node.catalogItemGroupData.tileViewItem && (
            <ListItem key={item.node.catalogItemGroupData.tileViewItem?.title || '' + index} {...item} {...restProps} />
          )
        );
      })}
    </GridContainer>
  );
};
